import { Box, Typography, IconButton, Alert, AlertTitle, CircularProgress } from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import moment from 'moment';

import { FHAntdDateTimePicker, FHTooltip } from '../../../../components/base';
import { TreatmentPlanStatus } from '../../../../components/layout/treatment-plan/constants/general';
import {
  FORTA_GRAY3,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_ERROR_3,
  FORTA_GRAY1,
  FORTA_GRAY2,
  FORTA_ORANGE,
  FORTA_ORANGE_3,
  FORTA_SUCCESS,
  FORTA_SUCCESS_3,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { formatEntityId } from '../../../../helpers';
import { ENTITY_PREFIX } from '../../../../constants';
import { ACTIVATION_DATE_FORMAT } from './constants';
import {
  disableActivationEndDate,
  disableActivationEndTime,
  disableActivationStartDate,
  disableActivationStartTime,
} from './utils';

import type { Moment } from 'moment';
import type { ActivationLimit, ActivationPeriod, TPActivationItem } from './TPActivationChart';
interface TPActivationEditFormProps {
  clientName: string;
  activationLogLoading: boolean;
  startDate: Moment | null;
  endDate: Moment | null;
  selectedTPId: number;
  startDateUpdateError: string | null;
  endDateUpdateError: string | null;
  selectedActivationPeriod: ActivationPeriod;
  tpActivationData: TPActivationItem[];
  activationStartDateLimit: ActivationLimit;
  activationEndDateLimit: ActivationLimit;
  handleSelectStartDateTime: (value: Moment) => void;
  handleSelectEndDateTime: (value: Moment) => void;
  setStartDateUpdateError: (error: string | null) => void;
  setEndDateUpdateError: (error: string | null) => void;
}

function ActivationErrorAlert({ error, onClose }: { error: string; onClose: () => void }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        borderRadius: '0 0 8px 8px',
        backgroundColor: FORTA_GRAY1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: FORTA_ERROR_3,
          border: `1px solid ${FORTA_ERROR}`,
          borderRadius: '8px',
          padding: '12px 16px',
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 500,
            flex: 1,
          }}
        >
          {error}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onClose()}
          sx={{
            padding: '4px',
            color: FORTA_ERROR,
            '&:hover': {
              backgroundColor: 'rgba(244, 72, 72, 0.1)',
            },
          }}
        >
          <Close sx={{ fontSize: '20px' }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function TPActivationEditForm({
  clientName,
  activationLogLoading = false,
  startDateUpdateError,
  endDateUpdateError,
  setStartDateUpdateError,
  setEndDateUpdateError,
  startDate,
  endDate,
  selectedTPId,
  selectedActivationPeriod,
  tpActivationData,
  activationStartDateLimit,
  activationEndDateLimit,
  handleSelectStartDateTime,
  handleSelectEndDateTime,
}: TPActivationEditFormProps) {
  const tpData = tpActivationData.find((tp) => tp.tpId === selectedTPId);
  if (!tpData) return null;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 4 }}>
        <Box>
          <Typography display="inline" fontWeight={700} mr={1}>
            Client:
          </Typography>
          <Typography
            display="inline"
            fontWeight={700}
            sx={{
              border: `1px solid ${FORTA_GRAY2}`,
              backgroundColor: FORTA_BLUE.lightest,
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              fontWeight: 700,
              color: FORTA_BLUE.primary,
              mr: 4,
            }}
          >
            {clientName}
          </Typography>
          <Typography display="inline" fontWeight={700} mr={1}>
            TP:
          </Typography>
          <Typography
            display="inline"
            sx={{
              border: `1px solid ${FORTA_GRAY2}`,
              backgroundColor: FORTA_BLUE.lightest,
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              mr: 4,
              fontWeight: 700,
              color: FORTA_BLUE.primary,
            }}
          >{` ${formatEntityId(selectedTPId, ENTITY_PREFIX.TreatmentPlan)}`}</Typography>
        </Box>
        <Box>
          <Typography display="inline" fontWeight={700} mr={1}>
            Status:
          </Typography>
          <Typography
            display="inline"
            sx={{
              px: 1,
              py: 0.5,
              ml: 1,
              borderRadius: '4px',
              border: `1px solid ${FORTA_GRAY2}`,
              fontWeight: 700,
              color:
                tpData.status === TreatmentPlanStatus.Completed
                  ? FORTA_SUCCESS
                  : tpData.status === TreatmentPlanStatus.Readonly
                    ? FORTA_ERROR
                    : FORTA_ORANGE,
              backgroundColor:
                tpData.status === TreatmentPlanStatus.Completed ? FORTA_SUCCESS_3 : FORTA_ORANGE_3,
            }}
          >
            {tpData.status?.replace('_', ' ')}
            {tpData.status === TreatmentPlanStatus.Readonly && (
              <Typography
                display="inline"
                fontSize={12}
                fontWeight={500}
                ml={1}
                color={FORTA_BLUE.primary}
              >
                (Historical TP)
              </Typography>
            )}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          mb={4}
          mt={3}
          sx={{
            border: `1px solid ${FORTA_GRAY3}`,
            height: 'fit-content',
            borderRadius: '8px',
            width: '50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 1,
              py: 1,
              px: 2,
              borderRadius: '8px 8px 0 0',
              borderBottom: `1px solid ${FORTA_GRAY2}`,
              backgroundColor: FORTA_GRAY1,
            }}
          >
            <Typography
              width={'100%'}
              textAlign={'center'}
              color={FORTA_BLUE.primary}
              fontWeight={700}
            >
              Activation Start
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              py: 1,
              px: 2,
              borderBottom: `1px solid ${FORTA_GRAY2}`,
            }}
          >
            <Typography width={'20%'} fontWeight={700} color={FORTA_BLUE.primary}>
              Current
            </Typography>
            <Typography width={'80%'} textAlign={'center'} ml={-5}>{`${moment(
              selectedActivationPeriod?.activationStart,
            ).format(ACTIVATION_DATE_FORMAT)}`}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              borderRadius: '0 0 8px 8px',
              gap: 2,
              py: 1,
              px: 2,
              backgroundColor: FORTA_GRAY1,
            }}
          >
            <Typography width={'20%'} color={FORTA_BLUE.primary} fontWeight={700} mt={0.75}>
              New
            </Typography>
            <Box sx={{ width: '80%', display: 'flex', alignItems: 'center', gap: 1 }}>
              {activationStartDateLimit.date ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <FHAntdDateTimePicker
                      format={ACTIVATION_DATE_FORMAT}
                      showTime={{ format: 'h:mm a' }}
                      sx={{
                        width: '100%',
                        boxShadow: 'none',
                      }}
                      allowClear={false}
                      showNow={false}
                      fullWidth
                      style={{ flexGrow: 1 }}
                      inputHeight={40}
                      disabled={activationLogLoading}
                      use12Hours
                      value={startDate ?? ''}
                      disabledDate={(current: Moment | undefined) =>
                        disableActivationStartDate(
                          current,
                          activationStartDateLimit,
                          selectedActivationPeriod,
                          endDate,
                        )
                      }
                      disabledTime={(current: Moment | undefined) =>
                        disableActivationStartTime(
                          current,
                          activationStartDateLimit,
                          selectedActivationPeriod,
                          endDate,
                        )
                      }
                      getPopupContainer={(trigger: any) => trigger?.parentElement || document.body}
                      onChange={handleSelectStartDateTime}
                      onSelect={handleSelectStartDateTime}
                      error={startDateUpdateError}
                    />
                  </Box>
                </>
              ) : (
                <Box>
                  <Typography textAlign="center" ml="80%" color={FORTA_ERROR} mt={0.75}>
                    Cannot update
                  </Typography>
                </Box>
              )}
              <FHTooltip
                arrow
                title={
                  activationStartDateLimit.date ? (
                    <Alert severity="info">
                      <AlertTitle>
                        {`Activation start date must be after ${moment(
                          activationStartDateLimit.date,
                        ).format(ACTIVATION_DATE_FORMAT)}.`}
                      </AlertTitle>
                      {activationStartDateLimit.reason && (
                        <Typography
                          color={FORTA_BLUE.primary}
                          fontSize={14}
                          fontWeight={600}
                        >{`(${activationStartDateLimit.reason})`}</Typography>
                      )}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      <AlertTitle>Activation start date cannot be updated</AlertTitle>
                      {activationStartDateLimit.reason && (
                        <Typography
                          color={FORTA_BLUE.primary}
                          fontSize={14}
                          fontWeight={600}
                        >{`(${activationStartDateLimit.reason})`}</Typography>
                      )}
                    </Alert>
                  )
                }
                placement="top"
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
                tooltipBorderRadius="8px"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    padding: '12px 16px',
                    maxWidth: '650px',
                  },
                }}
              >
                <InfoOutlined
                  sx={{
                    color: FORTA_BLUE.primary,
                    fontSize: '20px',
                    cursor: 'pointer',
                    mt: 0.75,
                  }}
                />
              </FHTooltip>
            </Box>
          </Box>
          {/* Error Messages */}
          {startDateUpdateError && (
            <ActivationErrorAlert
              error={startDateUpdateError}
              onClose={() => setStartDateUpdateError(null)}
            />
          )}
        </Box>
        <Box
          mb={4}
          mt={3}
          sx={{
            border: `1px solid ${FORTA_GRAY3}`,
            height: 'fit-content',
            borderRadius: '8px',
            width: '50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              borderRadius: '8px 8px 0 0',
              mb: 1,
              py: 1,
              px: 2,
              borderBottom: `1px solid ${FORTA_GRAY2}`,
              backgroundColor: FORTA_GRAY1,
            }}
          >
            <Typography
              width={'100%'}
              color={FORTA_BLUE.primary}
              textAlign={'center'}
              fontWeight={700}
            >
              Activation End
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              py: 1,
              px: 2,
              borderBottom: `1px solid ${FORTA_GRAY2}`,
            }}
          >
            <Typography width={'20%'} fontWeight={700} color={FORTA_BLUE.primary}>
              Current
            </Typography>
            <Typography width={'80%'} textAlign={'center'} ml={-5}>{`${
              selectedActivationPeriod?.activationEnd
                ? moment(selectedActivationPeriod?.activationEnd).format(ACTIVATION_DATE_FORMAT)
                : 'Currently active'
            }`}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              borderRadius: '0 0 8px 8px',
              gap: 2,
              py: 1,
              px: 2,
              backgroundColor: FORTA_GRAY1,
            }}
          >
            <Typography width={'20%'} fontWeight={700} color={FORTA_BLUE.primary} mt={0.75}>
              New
            </Typography>
            <Box
              sx={{ width: '80%', display: 'flex', height: '100%', alignItems: 'center', gap: 1 }}
            >
              {activationEndDateLimit.date ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <FHAntdDateTimePicker
                      format={ACTIVATION_DATE_FORMAT}
                      showTime={{ format: 'h:mm a' }}
                      sx={{
                        width: '100%',
                        boxShadow: 'none',
                      }}
                      allowClear={false}
                      showNow={false}
                      fullWidth
                      style={{ flexGrow: 1 }}
                      inputHeight={40}
                      disabled={activationLogLoading}
                      use12Hours
                      value={endDate ?? ''}
                      disabledDate={(current: Moment | undefined) =>
                        disableActivationEndDate(
                          current,
                          activationEndDateLimit,
                          selectedActivationPeriod,
                          startDate,
                        )
                      }
                      disabledTime={(current: Moment | undefined) =>
                        disableActivationEndTime(
                          current,
                          activationEndDateLimit,
                          selectedActivationPeriod,
                          startDate,
                        )
                      }
                      getPopupContainer={(trigger: any) => trigger?.parentElement || document.body}
                      onChange={handleSelectEndDateTime}
                      onSelect={handleSelectEndDateTime}
                      error={endDateUpdateError}
                    />
                  </Box>
                </>
              ) : (
                <Typography textAlign="center" height="35px" ml={6} color={FORTA_ERROR} mt={1}>
                  Currently active
                </Typography>
              )}
              <FHTooltip
                arrow
                title={
                  activationEndDateLimit.date ? (
                    <Alert severity="info">
                      <AlertTitle>
                        {`Activation end date cannot go beyond ${moment(
                          activationEndDateLimit.date,
                        ).format(ACTIVATION_DATE_FORMAT)}.`}
                      </AlertTitle>
                      {activationEndDateLimit.reason && (
                        <Typography
                          color={FORTA_BLUE.primary}
                          fontSize={14}
                          fontWeight={600}
                        >{`(${activationEndDateLimit.reason})`}</Typography>
                      )}
                    </Alert>
                  ) : (
                    <Alert severity="warning">
                      <AlertTitle>Activation end date cannot be updated</AlertTitle>
                      {activationEndDateLimit.reason && (
                        <Typography
                          color={FORTA_BLUE.primary}
                          fontSize={14}
                          fontWeight={600}
                        >{`(${activationEndDateLimit.reason})`}</Typography>
                      )}
                    </Alert>
                  )
                }
                placement="top"
                tooltipbackground={FORTA_WHITE}
                tooltipBoxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
                tooltipBorderRadius="8px"
                sx={{
                  '& .MuiTooltip-tooltip': {
                    padding: '12px 16px',
                    maxWidth: '650px',
                  },
                }}
              >
                <InfoOutlined
                  sx={{
                    color: FORTA_BLUE.primary,
                    fontSize: '20px',
                    cursor: 'pointer',
                    mt: 0.75,
                  }}
                />
              </FHTooltip>
            </Box>
          </Box>
          {endDateUpdateError && (
            <ActivationErrorAlert
              error={endDateUpdateError}
              onClose={() => setEndDateUpdateError(null)}
            />
          )}
        </Box>
      </Box>
      {activationLogLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

export default TPActivationEditForm;
